import { useState } from "react";

const withoutKey = (obj, key) => {
  const next = { ...obj };
  delete next[key];
  return next;
};

// Sends every unsaved edited line to Priority; calls onSaved(k) per line.
const saveItemEdits = async ({ order, items, editingKeys, savedKeys, edits, updateOrderItem, onSaved }) => {
  for (const k of editingKeys) {
    if (savedKeys.has(k)) continue;
    const edit = edits[k] || {};
    const updates = {};
    if (edit.NEO_AGENTDISC !== undefined && edit.NEO_AGENTDISC !== "") updates.NEO_AGENTDISC = Number(edit.NEO_AGENTDISC);
    const item = items[Number(k)];
    if (Object.keys(updates).length > 0 && updateOrderItem && item?.KLINE != null) {
      await updateOrderItem(order.ORDNAME, item.KLINE, updates);
    }
    onSaved(k);
  }
};

// Per-line editing of an order's items (keys = line index as string).
// Saved edits are kept as display overrides so the new values stay visible.
const useOrderItemEdits = ({ order, items, updateOrderItem }) => {
  const [editingKeys, setEditingKeys] = useState(new Set());
  const [edits, setEdits] = useState({});
  const [savedKeys, setSavedKeys] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const hasDirty = [...editingKeys].some((k) => !savedKeys.has(k) && edits[k] && Object.keys(edits[k]).length > 0);

  const toggleEdit = (k, e) => {
    e.stopPropagation();
    const closing = editingKeys.has(k);
    setEditingKeys((prev) => {
      const next = new Set(prev);
      closing ? next.delete(k) : next.add(k);
      return next;
    });
    // Closing an unsaved line discards its changes.
    if (closing && !savedKeys.has(k)) setEdits((prev) => withoutKey(prev, k));
    setSaveError(null);
  };

  const changeField = (k, field, value) => {
    setEdits((prev) => ({ ...prev, [k]: { ...(prev[k] || {}), [field]: value } }));
    setSavedKeys((prev) => new Set([...prev].filter((key) => key !== k)));
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const onSaved = (k) => setSavedKeys((prev) => new Set([...prev, k]));
      await saveItemEdits({ order, items, editingKeys, savedKeys, edits, updateOrderItem, onSaved });
      setEditingKeys(new Set());
    } catch (err) {
      setSaveError(err?.message || "שגיאה בשמירה");
    } finally {
      setSaving(false);
    }
  };

  return { editingKeys, edits, savedKeys, saving, saveError, hasDirty, toggleEdit, changeField, save };
};

export default useOrderItemEdits;
